/**
 * PrototypeArtifact — the chat/case-file card for a `ui_prototype` artifact.
 *
 * A scaled LIVE preview (no screenshot service — the sandboxed frame IS the
 * thumbnail, free and always current), the serif title, a mono revision
 * count, and the worker credit line when a Team's build credited one.
 * Glass material: a Prototype is staged work, not committed record.
 *
 * No html fetch: the frame iframes the backend's `/api/prototypes/:id/frame`
 * endpoint directly (session cookie rides the same-origin request), which
 * serves the bytes CSP-sandboxed into an opaque origin.
 */
import { Link } from "react-router-dom";
import { PrototypeFrame, prototypeFrameUrl } from "./PrototypeFrame.js";

export interface PrototypeArtifactData {
  prototypeId: string;
  title: string;
  currentRevision: number;
  url: string;
  workItemId?: string;
  creditedWorker?: string;
}

interface Props {
  data: PrototypeArtifactData;
  /**
   * Open the review where the card sits instead of navigating to the
   * prototype's page (the case file's in-place review). Absent, the card is a
   * link.
   */
  onOpen?: () => void;
  /** The kind's label in the card's kicker; "Prototype" when absent. */
  kindLabel?: string;
  /** The credit line; absent, the credited Worker's line as today. */
  byline?: string;
  /** False when the actions sit beside the card (the case file's Design pane). */
  showFoot?: boolean;
}

/** 'wright' → 'Wright' — worker slugs are lowercase guild surnames. */
function workerDisplayName(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

export function PrototypeArtifact({ data, onOpen, kindLabel = "Prototype", byline, showFoot = true }: Props) {
  const card = (
    <div
      className="mt-1.5 material-glass px-4 py-3 transition-shadow hover:shadow-[var(--shadow-staged-high)]"
      role="article"
      aria-label="Prototype artifact"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="w-2 h-2 rounded-full bg-[var(--accent)] flex-shrink-0"
          aria-hidden="true"
        />
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--accent)]">
          {kindLabel}
        </span>
        <span className="ml-auto font-mono text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">
          Rev {data.currentRevision}
        </span>
      </div>
      <div className="rounded-[var(--radius-control)] border border-[var(--border-subtle)] overflow-hidden mb-2 bg-[var(--brand-white)]">
        <PrototypeFrame
          src={prototypeFrameUrl(data.prototypeId, data.currentRevision)}
          title={data.title}
          mode="card"
        />
      </div>
      <div
        className="text-sm font-semibold text-[var(--text-primary)] leading-snug"
        style={{ fontFamily: "var(--font-serif)" }}
      >
        {data.title}
      </div>
      {byline || data.creditedWorker ? (
        <p className="mt-1 text-[12px] text-[var(--text-secondary)]">
          {byline ?? `${workerDisplayName(data.creditedWorker ?? "")} prototyped this`}
        </p>
      ) : null}
      {showFoot && (
        <div className="mt-2 text-[11px] text-[var(--accent)] font-medium">
          {onOpen ? "Review here" : "Open prototype"}
        </div>
      )}
    </div>
  );
  if (onOpen) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left bg-transparent border-0 p-0 cursor-pointer"
        data-testid="prototype-artifact"
        aria-label={`Review ${data.title}`}
      >
        {card}
      </button>
    );
  }
  return (
    <Link
      to={data.url}
      className="block no-underline"
      data-testid="prototype-artifact"
    >
      {card}
    </Link>
  );
}
